import { TFunction } from 'i18next';
import React, { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { v4 } from 'uuid';

import { GeodataBoundaryPolygonModel } from '../../../../../typings/api/skymap/rest/v0/geodata';
import { isDefined } from '../../../utils/variables';
import { Icon } from '../icon/icon';

interface StatusIconProps {
  model: GeodataBoundaryPolygonModel;
  onToggleEnabled?: (id: string) => void;
  t: TFunction;
}

export const StatusIcon: React.FC<StatusIconProps> = ({ model, onToggleEnabled, t }) => {
  const theme = useTheme();

  const title = useMemo<string | undefined>(() => {
    if (isDefined(onToggleEnabled)) {
      return model.enabled
        ? t('boundaryPolygonList.status.enabled', { ns: 'cloudProcessing' })
        : t('boundaryPolygonList.status.disabled', { ns: 'cloudProcessing' });
    }

    return undefined;
  }, [model, t, onToggleEnabled]);

  // If FAILED, show red exclamation
  if (model.status === 'FAILED') {
    return (
      <Icon
        color={theme.color.red}
        icon={['fas', 'exclamation-circle']}
        key={v4()}
        title={t('boundaryPolygonList.status.failed', {
          ns: 'cloudProcessing',
        })}
      />
    );
  }

  // If PENDING, show spinning clock
  if (model.status === 'PENDING') {
    return (
      <Icon
        color={theme.color.gray.medium}
        icon={['fal', 'spinner']}
        key={v4()}
        spin={true}
        title={t('boundaryPolygonList.status.pending', {
          ns: 'cloudProcessing',
        })}
      />
    );
  }

  // If COMPLETED, show green/gray based on enabled
  return (
    <Icon
      color={model.enabled ? theme.color.green : theme.color.gray.medium}
      icon={['fas', 'check-circle']}
      key={v4()}
      style={{ cursor: onToggleEnabled ? 'pointer' : 'default' }}
      title={title}
      onClick={onToggleEnabled ? () => onToggleEnabled(model.id) : undefined}
    />
  );
};

interface BoundaryTypeIconProps {
  model: GeodataBoundaryPolygonModel;
  onToggleBoundaryType?: (id: string) => void;
  t: TFunction;
}

export const BoundaryTypeIcon: React.FC<BoundaryTypeIconProps> = ({
  model,
  onToggleBoundaryType,
  t,
}) => {
  return (
    <Icon
      color={model.boundaryType === 'Outer' ? 'blue' : 'red'}
      icon={['fal', 'triangle']}
      key={v4()}
      style={{ cursor: onToggleBoundaryType ? 'pointer' : 'default' }}
      title={
        model.boundaryType === 'Outer'
          ? t('boundaryPolygonList.type.outer', { ns: 'cloudProcessing' })
          : t('boundaryPolygonList.type.inner', { ns: 'cloudProcessing' })
      }
      onClick={onToggleBoundaryType ? () => onToggleBoundaryType(model.id) : undefined}
      onHoverStyle={{ icon: ['fad', 'triangle'] }}
    />
  );
};
